import { useEffect, useState } from 'react'

import { useMatchConfig } from '@/hooks/useMatchConfig'

import type { JsonMatchData } from '@/services/jsonDataService'
import type { MatchConfig } from '@/services/matchConfigService'

interface CombinedMatchData extends MatchConfig {
  jsonData: JsonMatchData
}

interface UseMatchDataReturn {
  data: CombinedMatchData | null
  isLoading: boolean
  error: Error | null
}

export const useMatchData = (customerId: string, eventGuid: string): UseMatchDataReturn => {
  const { data: matchConfig, isLoading: configLoading, error: configError } = useMatchConfig(customerId, eventGuid)
  const [data, setData] = useState<CombinedMatchData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (configError) {
      setError(configError)
      setIsLoading(false)
      return
    }

    if (configLoading || !matchConfig || !customerId || !eventGuid) {
      setIsLoading(configLoading)
      return
    }

    const loadMatchData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Fetch JSON data to get sport info
        const { fetchJsonMatchData } = await import('@/services/jsonDataService')
        const jsonData = await fetchJsonMatchData()

        // Combine config with JSON data
        const combinedData = {
          ...matchConfig,
          jsonData,
        }

        setData(combinedData)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch match data'))
      } finally {
        setIsLoading(false)
      }
    }

    loadMatchData()
  }, [matchConfig, configLoading, configError, customerId, eventGuid])

  return { data, isLoading, error }
}
